import { useEffect, useState } from "react";
import { FirestoreDocUser } from "breadcommon";
import { AppEntrance } from "./FullPageLoadingScreen";
import { callFirebaseFunction } from "./firebaseio/firebaseFunctions";
import { CancellationRecovery } from "./CancellationRecovery";
import { LoadUser } from "./LoadUser";
import { SubscriptionRequired } from "./SubscriptionRequired";
import { getAuth, signOut } from "firebase/auth";
import { RyeButton } from "./rye/RyeButton";
import { buildClasses } from "./utils/buildClasses";
import { prefetchInstitutionSearch } from "./accounts_screen/institutionSearchPrefetch";
import { useFontsReady } from "./fontsReady";

export function SubscriptionCheck(props: {
  user: FirestoreDocUser;
}): JSX.Element {
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [canSignIn, setCanSignIn] = useState(false);
  const [cancellationPending, setCancellationPending] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  // Fonts load alongside the check (RootRoute starts them), and this screen
  // stays up for whichever finishes last.
  const fontsReady = useFontsReady();

  useEffect(() => {
    // The bank search behind Add Account is slow when its functions first
    // start up. Starting them, and fetching the bank grid, during this loading
    // screen makes it quick by the time anyone links an account; most of all
    // for new users, whose first step that is. It also runs for users who turn
    // out not to be subscribed, which is harmless.
    prefetchInstitutionSearch();

    const checkSubscription = async () => {
      try {
        const minLoadingTime = new Promise((resolve) =>
          setTimeout(resolve, 1000),
        );
        const subscriptionCheck = callFirebaseFunction("StripeSignInCheck", {});

        const [, result] = await Promise.all([
          minLoadingTime,
          subscriptionCheck,
        ]);

        const data = result.data;

        if (data.success) {
          setCancellationPending(data.cancellationPending ?? false);
          setCanSignIn(data.canSignIn ?? false);
          setSubscriptionId(data.subscriptionId ?? null);
          setSubscriptionStatus(data.status ?? null);
        } else {
          setError(data.error_msg || "Failed to check subscription status");
        }
      } catch (err) {
        setError("Failed to check subscription status");
        console.error(err);
      } finally {
        setCheckingSubscription(false);
      }
    };

    checkSubscription();
  }, []);

  // Rendered inside AppEntrance, which keeps the loading screen up until
  // there's something to show.
  let content: JSX.Element | null = null;
  let entersApp = false;
  if (checkingSubscription || !fontsReady) {
    content = null;
  } else if (error) {
    content = (
      <div
        className={buildClasses(
          "min-h-screen",
          "bg-surface-50",
          "flex",
          "items-center",
          "justify-center",
          "p-4",
        )}
      >
        <div
          className={buildClasses(
            "max-w-md",
            "w-full",
            "bg-white",
            "rounded-lg",
            "shadow-lg",
            "p-6",
          )}
        >
          <h2
            className={buildClasses(
              "text-xl",
              "font-semibold",
              "text-on-surface-900",
              "mb-4",
            )}
          >
            Something went wrong
          </h2>
          <div
            className={buildClasses(
              "p-4",
              "bg-red-50",
              "border",
              "border-red-200",
              "rounded-md",
              "text-red-800",
              "text-sm",
              "mb-4",
            )}
          >
            {error}
          </div>
          <div className={buildClasses("flex", "gap-3")}>
            <RyeButton
              text="Try Again"
              onClick={() => window.location.reload()}
              icon="refresh"
              vibe="primary"
              variant="filled"
            />
            <RyeButton
              text="Sign Out"
              onClick={async () => {
                const auth = getAuth();
                await signOut(auth);
              }}
              vibe="subdued"
            />
          </div>
        </div>
      </div>
    );
  } else if (cancellationPending) {
    content = <CancellationRecovery />;
  } else if (!canSignIn) {
    content = (
      <SubscriptionRequired
        subscriptionId={subscriptionId}
        subscriptionStatus={subscriptionStatus}
      />
    );
  } else {
    content = <LoadUser user={props.user} />;
    entersApp = true;
  }

  return (
    <AppEntrance ready={content !== null} entersApp={entersApp}>
      {content}
    </AppEntrance>
  );
}
